import { useState } from 'react'
import { Wallet, RefreshCw, Upload, Wrench, AlertTriangle } from 'lucide-react'
import { getAdminAddresses, adminAddAddresses, adminRepairAddresses } from '../../services/api'
import {
  C, Card, Row, Btn, Chips, Loading, ErrorLine, Empty, inputStyle, selectStyle, labelStyle,
  useAsync, fmtDate, Pill, divider, confirmAsync, NETWORK_LABELS,
} from './adminUI'

// Раздел «Адреса»: пул депозитных адресов для приёма оплат.
// Если в сети кончились свободные адреса — юзер не сможет создать заявку,
// поэтому пустой пул подсвечивается красным.
//   import — добавить новые адреса, существующие не трогать (обычный случай);
//   sync   — привести пул сети РОВНО к переданному списку (лишние удаляются,
//            но адрес с незакрытой заявкой не удаляется никогда).
// «Починить пул» (repairAddressPool) правит пул в ОБЕ стороны (2026-09-28):
//   free, но есть pending-заявка  → in_use (иначе выдача упала бы с UNIQUE);
//   in_use, а незакрытых заявок нет → free (адрес «завис» и пропадал из пула).
// Возвращает число исправленных строк. Ту же починку раз в час делает
// планировщик (Node — api.js, Python — payment_bot.expire_pending_payments),
// поэтому уже застрявшие адреса вернутся в пул сами, без нажатия кнопки.

const NETWORKS = ['TRC20', 'BEP20', 'SOL_USDT', 'TON']

export default function AddressesSection() {
  const [network, setNetwork] = useState('TRC20')
  const [text, setText] = useState('')
  const [mode, setMode] = useState('import')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')

  const { data, loading, error, reload } = useAsync(() => getAdminAddresses(network), [network])

  const pool = data?.pool || []
  const list = data?.list || []
  const cur = pool.find(p => p.network === network)

  const submit = async () => {
    const items = text.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
    if (!items.length) { setNote('Ошибка: список адресов пуст'); return }
    if (mode === 'sync' && !(await confirmAsync(
      `Привести пул ${network} ровно к списку из ${items.length} адресов? Отсутствующие в списке свободные адреса будут удалены.`
    ))) return
    setBusy(true); setNote('')
    try {
      const r = await adminAddAddresses(network, items, mode)
      const res = r?.result
      setNote(mode === 'sync'
        ? `Синхронизировано ${network}: добавлено ${res?.added ?? 0}, удалено ${res?.removed ?? 0}`
        : `Добавлено новых адресов ${network}: ${typeof res === 'number' ? res : (res?.added ?? 0)}`)
      setText('')
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(false) }
  }

  const repair = async () => {
    setBusy(true); setNote('')
    try {
      const r = await adminRepairAddresses()
      // repairAddressPool() возвращает число исправленных адресов (обе стороны,
      // см. комментарий в шапке файла).
      const n = typeof r?.result === 'number' ? r.result : (r?.result?.repaired ?? 0)
      setNote(n ? `Пул починен: исправлено адресов — ${n}` : 'Пул в порядке, исправлять нечего')
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(false) }
  }

  return (
    <div>
      <ErrorLine onRetry={reload}>{error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      <Card
        title="Состояние пула"
        icon={Wallet}
        right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>}
      >
        {loading && !pool.length ? <Loading /> : !pool.length ? <Empty>Адресов не загружено ни в одну сеть</Empty> : (
          pool.map(p => (
            <Row
              key={p.network}
              label={NETWORK_LABELS[p.network] || p.network}
              value={`${p.free} свободно`}
              valueColor={p.free === 0 ? C.danger : p.free < 3 ? C.warn : C.accent}
              valueSub={`занято ${p.inUse} · всего ${p.total} · заявок ждёт ${p.pending}`}
            />
          ))
        )}
        {pool.some(p => p.free === 0) && (
          <div style={{
            marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start',
            background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
            borderRadius: 12, padding: '9px 11px',
          }}>
            <AlertTriangle size={14} color={C.danger} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 11.5, color: C.danger, lineHeight: 1.45 }}>
              В сети без свободных адресов юзер не сможет создать заявку на оплату.
              Добавьте адреса ниже.
            </div>
          </div>
        )}
        <div style={divider} />
        <Btn small ghost icon={Wrench} color={C.warn} disabled={busy} onClick={repair}>Починить пул</Btn>
      </Card>

      <Card title="Добавить адреса" icon={Upload} accent={C.indigo}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Сеть</div>
            <select value={network} onChange={e => setNetwork(e.target.value)} style={selectStyle}>
              {NETWORKS.map(n => <option key={n} value={n}>{NETWORK_LABELS[n] || n}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Режим</div>
            <select value={mode} onChange={e => setMode(e.target.value)} style={selectStyle}>
              <option value="import">Добавить новые</option>
              <option value="sync">Синхронизировать</option>
            </select>
          </div>
        </div>
        <div style={labelStyle}>Адреса (по одному в строке)</div>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={5}
          placeholder={'TXk...\nTYn...'}
          style={{ ...inputStyle, resize: 'vertical', fontSize: 12.5, lineHeight: 1.5 }}
        />
        <div style={{ marginTop: 10 }}>
          <Btn icon={Upload} disabled={busy || !text.trim()} onClick={submit}>
            {busy ? '…' : mode === 'sync' ? 'Синхронизировать' : 'Добавить'}
          </Btn>
        </div>
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 10, lineHeight: 1.45 }}>
          «Добавить новые» пропускает адреса, которые уже есть в пуле.
          «Синхронизировать» удаляет свободные адреса, отсутствующие в списке.
        </div>
      </Card>

      <Card title={`Адреса ${NETWORK_LABELS[network] || network} (${list.length})`} icon={Wallet} accent={C.muted}>
        {loading ? <Loading /> : !list.length ? <Empty>В этой сети адресов нет</Empty> : (
          list.map((a, i) => (
            <div key={a.address} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              borderTop: i ? `1px solid ${C.line}` : 'none', padding: '8px 0',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11.5, color: '#fff', fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                  {a.address}
                </div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>добавлен {fmtDate(a.added_at)}</div>
              </div>
              <Pill color={a.status === 'free' ? C.accent : C.warn}>
                {a.status === 'free' ? 'свободен' : 'занят'}
              </Pill>
            </div>
          ))
        )}
        {cur && (
          <>
            <div style={divider} />
            <Row label="Свободно / всего" value={`${cur.free} / ${cur.total}`} />
          </>
        )}
      </Card>
    </div>
  )
}
